import { useEffect, useState, useId } from 'react';
import { Lock, Unlock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { parseMoney } from '@mibodega/domain';
import { formatMoneyInput } from '@/lib/format';
import {
  abrirSesionCajaOffline,
  cerrarSesionCajaOffline,
  type LineaDesglose,
  type SesionCaja,
} from '@/lib/cash';
import { encolarAperturaCaja, encolarCierreCaja } from '@/lib/sync-queue';
import { type OrgConfig } from '@/lib/org-config';
import { connector } from '@/lib/auth';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { TextField } from './TextField';

interface CashSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sesionActiva: SesionCaja | null;
  orgConfig: OrgConfig | null;
  onSessionChange: (sesion: SesionCaja | null) => void;
  onRetryConfig?: () => void | Promise<void>;
  /** Fuerza el modo de cierre cuando se abre desde el diálogo de caja anterior. */
  forceMode?: 'cerrar';
}

const formatNumber = (n: number, decimals = 2): string => {
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n).toFixed(decimals);
  const [intPart, decPart] = abs.split('.');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart ? `${sign}${withSep}.${decPart}` : `${sign}${withSep}`;
};

export function CashSessionModal({
  isOpen,
  onClose,
  sesionActiva,
  orgConfig,
  onSessionChange,
  onRetryConfig,
  forceMode,
}: CashSessionModalProps) {
  const titleId = useId();
  const descId = useId();

  const mode = forceMode === 'cerrar' || (sesionActiva && !forceMode) ? 'cerrar' : 'abrir';
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const monedas = orgConfig?.monedasOperativas ?? [];
  const baseMoneda = orgConfig?.monedaBase ?? 'COP';

  // Reset montos on open so each session starts clean.
  useEffect(() => {
    if (isOpen) setCantidades({});
  }, [isOpen]);

  const tasaPara = (moneda: string): number => {
    if (!orgConfig) return 1;
    if (moneda === baseMoneda) return 1;
    const t = orgConfig.tasas.find(
      (tt) => tt.monedaOrigen === moneda && tt.monedaDestino === baseMoneda,
    );
    return t?.tasa ?? 0;
  };

  const desglose: LineaDesglose[] = monedas.flatMap<LineaDesglose>((moneda) => {
    const raw = cantidades[moneda] ?? '';
    const monto = parseMoney(raw);
    if (!Number.isFinite(monto) || monto <= 0) return [];
    return [{ moneda, monto, tasa: tasaPara(moneda) }];
  });

  const totalEstimadoBase = desglose.reduce((acc, l) => acc + l.monto * (l.tasa ?? 1), 0);

  const monedasFaltantes = monedas.filter((m) => tasaPara(m) === 0 && m !== baseMoneda);

  const submitHabilitado =
    !isSubmitting && Boolean(orgConfig) && monedas.length > 0 && desglose.length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (desglose.length === 0) {
      toast.error('Ingresá al menos un monto por moneda');
      return;
    }
    for (const l of desglose) {
      if (l.tasa === 0) {
        toast.error(`Falta tasa de cambio para ${l.moneda}. Pedile al admin.`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (mode === 'abrir') {
        const orgId = orgConfig?.orgId ?? '';
        const sucursalId = connector.getCurrentSucursalId() ?? orgConfig?.sucursalId ?? null;
        if (!orgId || !sucursalId) {
          throw new Error('Org o sucursal no configuradas. Pedile al admin configurar.');
        }
        const userId = connector.getCurrentUser()?.id;
        if (!userId) {
          throw new Error('No hay usuario autenticado. Iniciá sesión.');
        }
        const nuevaSesion = await abrirSesionCajaOffline({
          organizationId: orgId,
          sucursalId,
          usuarioId: userId,
          monedaBase: baseMoneda,
          desglose: desglose,
          encolarSubida: true,
        });
        await encolarAperturaCaja({
          sesion: nuevaSesion,
          organizationId: orgId,
          sucursalId,
          usuarioId: userId,
          monedaBase: baseMoneda,
          desglose: desglose,
          idempotencyKey: nuevaSesion.idempotency_key_apertura,
        });
        toast.success(
          navigator.onLine
            ? `Caja abierta con ${nuevaSesion.desglose_apertura.length} moneda(s) · sincronizando…`
            : `Caja abierta con ${nuevaSesion.desglose_apertura.length} moneda(s). Sube al reconectar.`,
        );
        onSessionChange(nuevaSesion);
      } else if (sesionActiva) {
        const updated = await cerrarSesionCajaOffline({
          sesionId: sesionActiva.id,
          desglose: desglose,
          encolarSubida: true,
        });
        await encolarCierreCaja({
          sesion: sesionActiva,
          organizationId: sesionActiva.organization_id,
          desglose: desglose,
          idempotencyKey: updated.idempotency_key_cierre ?? crypto.randomUUID(),
        });
        toast.success(
          navigator.onLine
            ? 'Caja cerrada · arqueo sincronizando…'
            : 'Caja cerrada. Arqueo se sube al reconectar.',
        );
        onSessionChange(null);
      }
      setCantidades({});
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Error: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {mode === 'abrir' ? (
            <Unlock className="text-success h-5 w-5" aria-hidden="true" />
          ) : (
            <Lock className="text-danger h-5 w-5" aria-hidden="true" />
          )}
          {mode === 'abrir' ? 'Apertura de Turno (Caja)' : 'Cierre de Turno y Arqueo'}
        </span>
      }
      titleId={titleId}
      descriptionId={descId}
      description={
        mode === 'abrir'
          ? 'Ingresá el fondo de caja por moneda. Lo vas a poder cerrar al final del turno.'
          : 'Contá el efectivo en caja y registrá el desglose por moneda.'
      }
      size="md"
      footer={
        orgConfig ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              variant={mode === 'abrir' ? 'primary' : 'danger'}
              onClick={() => {
                void handleSubmit({ preventDefault: () => {} } as React.FormEvent);
              }}
              disabled={!submitHabilitado}
              loading={isSubmitting}
              loadingLabel="Procesando…"
            >
              {mode === 'abrir' ? 'Confirmar Apertura' : 'Confirmar Cierre & Arqueo'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        )
      }
    >
      {!orgConfig ? (
        <div className="space-y-3">
          <div className="border-warning bg-warning-soft text-warning rounded-md border p-4 text-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <div className="flex-1 space-y-1">
                <p className="font-semibold">No se pudo cargar la configuración de la tienda.</p>
                <p className="text-xs">
                  Para abrir caja necesitamos la moneda base y las tasas vigentes. Verificá tu
                  conexión o iniciá sesión nuevamente.
                </p>
                {onRetryConfig && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      void onRetryConfig();
                    }}
                    className="mt-3"
                  >
                    Reintentar carga de configuración
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            {monedas.map((moneda) => {
              const sinTasa = tasaPara(moneda) === 0 && moneda !== baseMoneda;
              const labelText =
                moneda === baseMoneda
                  ? `${moneda} (moneda base)`
                  : `${moneda} · 1 ${moneda} = ${formatNumber(tasaPara(moneda), 4)} ${baseMoneda}`;
              return (
                <TextField
                  key={moneda}
                  id={`cash-${moneda}`}
                  label={labelText}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  disabled={sinTasa}
                  placeholder={moneda === baseMoneda ? '0,00' : '0,00 (no disponible)'}
                  value={cantidades[moneda] ?? ''}
                  onChange={(e) =>
                    setCantidades((prev) => ({
                      ...prev,
                      [moneda]: formatMoneyInput(e.target.value),
                    }))
                  }
                  className="tnum font-mono text-base font-semibold"
                  aria-label={`Monto en ${moneda}${moneda === baseMoneda ? ' (moneda base)' : ''}`}
                  helperText={sinTasa ? 'Falta tasa · pedile al admin que la cargue' : undefined}
                />
              );
            })}
          </div>

          <div className="rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
            Total estimado:{' '}
            <span className="tnum font-mono font-semibold text-neutral-900">
              {baseMoneda} {formatNumber(totalEstimadoBase)}
            </span>
            <p className="mt-0.5 text-[11px] text-neutral-500">
              El server recalcula con la tasa vigente al sincronizar.
            </p>
          </div>

          {monedasFaltantes.length > 0 && (
            <div className="border-warning bg-warning-soft text-warning rounded-md border px-3 py-2 text-xs">
              <p>
                <AlertTriangle className="mr-1 inline h-3 w-3" aria-hidden="true" />
                Tasas pendientes para: <strong>{monedasFaltantes.join(', ')}</strong>. Pedile al
                admin que las cargue desde web-admin.
              </p>
            </div>
          )}

          {mode === 'cerrar' && sesionActiva && (
            <details className="rounded-md bg-neutral-50 p-3 text-xs text-neutral-600">
              <summary className="cursor-pointer font-semibold text-neutral-900">
                Resumen del turno
              </summary>
              <ul className="mt-2 space-y-1">
                <li>
                  Apertura:{' '}
                  <span className="font-semibold">
                    {new Date(sesionActiva.fecha_apertura).toLocaleString()}
                  </span>
                </li>
                <li>
                  Fondo apertura:{' '}
                  <span className="font-semibold">
                    {sesionActiva.moneda_base_codigo} {formatNumber(sesionActiva.monto_inicial)}
                  </span>
                </li>
                <li>
                  Moneda base del turno:{' '}
                  <span className="font-semibold">{sesionActiva.moneda_base_codigo}</span>
                </li>
              </ul>
            </details>
          )}
        </form>
      )}
    </Dialog>
  );
}
